import { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import CustomHelmet from "./CustomHelmet";

const destinations = [
  {
    title: "Services",
    description: "What I help with, from couples counseling to autism support",
    to: "/services",
  },
  {
    title: "Blog",
    description: "Plain answers on the things people bring into my office",
    to: "/blog",
  },
  {
    title: "Rates & Insurance",
    description: "Session costs, self-pay options, and accepted insurance",
    to: "/rates",
  },
  {
    title: "Contact",
    description: "Call, text, or send a message, whatever feels easiest",
    to: "/contact",
  },
];

function NotFoundPage() {
  useEffect(() => {
    // Add 404 and robots meta for prerenderers and SEO hygiene
    const head = document.getElementsByTagName("head")[0];
    const prerenderMeta = document.createElement("meta");
    prerenderMeta.name = "prerender-status-code";
    prerenderMeta.content = "404";
    head.appendChild(prerenderMeta);

    const robotsMeta = document.createElement("meta");
    robotsMeta.name = "robots";
    robotsMeta.content = "noindex, nofollow";
    head.appendChild(robotsMeta);

    return () => {
      if (prerenderMeta.parentNode === head) head.removeChild(prerenderMeta);
      if (robotsMeta.parentNode === head) head.removeChild(robotsMeta);
    };
  }, []);

  return (
    <Box component="main" sx={{ py: { xs: 5, sm: 8 }, px: 2 }}>
      <CustomHelmet
        title="Page not found (404) | Dr. Anne Rothenberg"
        description="The page you're looking for doesn't exist. Here are helpful links to get you back on track."
        ogImage="https://drannerothenberg.com/dranne-rothenberg.jpg"
      />
      <Container maxWidth="md">
        <Stack spacing={2.5} sx={{ alignItems: "center", textAlign: "center" }}>
          <Typography
            component="div"
            aria-hidden
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.04em",
              fontSize: "clamp(64px, 12vw, 120px)",
              lineHeight: 1,
              color: "primary.light",
            }}
          >
            404
          </Typography>

          <Typography
            variant="h1"
            component="h1"
            sx={{ fontSize: { xs: "1.75rem", sm: "2rem" }, mb: 0 }}
          >
            We couldn't find that page
          </Typography>

          <Typography color="text.secondary" sx={{ maxWidth: 560 }}>
            The link may be out of date, or the page may have moved. Either way,
            you're still in the right place. Here's where most people want to
            go.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              width: "100%",
              maxWidth: 640,
              textAlign: "left",
              my: 1,
            }}
          >
            {destinations.map((dest) => (
              <Card key={dest.to} variant="warm">
                <CardActionArea
                  component={RouterLink}
                  to={dest.to}
                  sx={{ p: 2.5, height: "100%" }}
                >
                  <Typography
                    component="h2"
                    variant="subtitle1"
                    sx={{ color: "headline.main", fontWeight: 600, mb: 0.5 }}
                  >
                    {dest.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {dest.description}
                  </Typography>
                </CardActionArea>
              </Card>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <Button
              variant="peach"
              component={RouterLink}
              to="/request-consultation"
            >
              Request a Free Consultation
            </Button>
            <Button
              variant="peachOutlined"
              component={RouterLink}
              to="/"
              startIcon={<HomeRoundedIcon />}
            >
              Return Home
            </Button>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

export default NotFoundPage;
